import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FieldErrors, UseFormWatch } from 'react-hook-form';

import Button from '@/components/Button';
import { Product } from '@/types/fulfill/inventory';

import { CreateKitFormData } from '../schema';

interface KitComponentField {
  id: string;
  sku: string;
  quantity: number;
}

interface CreateKitStepQuantitiesProps {
  step: number;
  setCurrentStep: (step: number) => void;
  fields: KitComponentField[];
  selectedProductsMap: Map<string, Product>;
  onChangeQuantity: (index: number, quantity: string) => void;
  errors: FieldErrors<CreateKitFormData>;
  watch: UseFormWatch<CreateKitFormData>;
  isDisabled: boolean;
}

const CreateKitStepQuantities = (props: CreateKitStepQuantitiesProps) => {
  const theme = useTheme();
  const liveComponents = props.watch('components') ?? [];
  const totalPieces = liveComponents.reduce(
    (sum, component) => sum + (component?.quantity || 0),
    0,
  );

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Indica cuántas piezas de cada producto forman parte del Kit.
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          borderRadius: '1rem',
          overflow: 'hidden',
        }}
      >
        {props.fields.map((field, index) => {
          const product = props.selectedProductsMap.get(field.sku);
          const errorMessage = props.errors.components?.[index]?.quantity?.message;
          return (
            <Box
              key={field.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: theme.palette.grey[100],
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <img
                  src={product?.thumbnail}
                  alt={product?.name}
                  style={{ width: 44, height: 44, borderRadius: '0.5rem', objectFit: 'cover' }}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {product?.name ?? field.sku}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                    SKU {field.sku}
                  </Typography>
                </Box>
              </Box>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={liveComponents[index]?.quantity ?? ''}
                onChange={(event) => props.onChangeQuantity(index, event.target.value)}
                error={Boolean(errorMessage)}
                helperText={errorMessage}
                inputProps={{ min: 1, step: 1, style: { textAlign: 'center' } }}
                sx={{ width: '100%' }}
              />
            </Box>
          );
        })}
      </Box>

      <Grid container justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" fontWeight="bold" color={theme.palette.primary.main}>
          Total de piezas: {totalPieces}
        </Typography>
        <Button disabled={props.isDisabled} onClick={() => props.setCurrentStep(props.step + 1)}>
          Continuar
        </Button>
      </Grid>
    </Box>
  );
};

export default CreateKitStepQuantities;
